import { ContentType, Uuid } from '@standardnotes/domain-core';
import { PayloadEmitSource, UIFeature, PrefKey, } from '@standardnotes/models';
import { FindNativeFeature, FindNativeTheme, GetIframeEditors, GetNativeThemes, NativeFeatureIdentifier, GetDeprecatedEditors, } from '@standardnotes/features';
import { Copy, removeFromArray, sleep, isNotUndefined, blobToBase64 } from '@standardnotes/utils';
import { ComponentViewer } from '@Lib/Services/ComponentManager/ComponentViewer';
import { AbstractService, isMobileDevice, PreferencesServiceEvent, FeatureStatus, LocalPrefKey, } from '@standardnotes/services';
import { GetFeatureUrl } from './UseCase/GetFeatureUrl';
import { ComponentManagerEvent } from './ComponentManagerEvent';
import { RunWithPermissionsUseCase } from './UseCase/RunWithPermissionsUseCase';
import { EditorForNoteUseCase } from './UseCase/EditorForNote';
import { GetDefaultEditorIdentifier } from './UseCase/GetDefaultEditorIdentifier';
import { DoesEditorChangeRequireAlertUseCase } from './UseCase/DoesEditorChangeRequireAlert';
import { c } from 'ttag';
/**
 * Responsible for orchestrating component functionality, including editors, themes,
 * and other components. The component manager primarily deals with iframes, and orchestrates
 * sending and receiving messages to and from frames via the postMessage API.
 */
export class ComponentManager extends AbstractService {
    constructor(items, mutator, sync, features, preferences, alerts, environment, platform, device, logger, internalEventBus) {
        super(internalEventBus);
        this.items = items;
        this.mutator = mutator;
        this.sync = sync;
        this.features = features;
        this.preferences = preferences;
        this.alerts = alerts;
        this.environment = environment;
        this.platform = platform;
        this.device = device;
        this.logger = logger;
        this.internalEventBus = internalEventBus;
        this.viewers = [];
        this.permissionDialogUIHandler = () => {
            throw 'Must call setPermissionDialogUIHandler';
        };
        this.runWithPermissionsUseCase = new RunWithPermissionsUseCase(this.permissionDialogUIHandler, this.alerts, this.mutator, this.sync, this.items);
        this.nativeThemesAsBase64 = {};
        this.detectFocusChange = () => {
            const activeIframes = Array.from(document.getElementsByTagName('iframe'));
            for (const iframe of activeIframes) {
                if (document.activeElement === iframe) {
                    setTimeout(() => {
                        const viewer = this.findComponentViewer(iframe.dataset.componentViewerId);
                        void this.notifyEvent(ComponentManagerEvent.ViewerDidFocus, {
                            componentViewer: viewer,
                        });
                    });
                    return;
                }
            }
        };
        this.onWindowMessage = (event) => {
            var _a;
            const data = event.data;
            if (data.sessionKey) {
                this.logger.info('Component manager received message', data);
                (_a = this.componentViewerForSessionKey(data.sessionKey)) === null || _a === void 0 ? void 0 : _a.handleMessage(data);
            }
        };
        this.loggingEnabled = false;
        this.addSyncedComponentItemObserver();
        this.registerMobileNativeComponentUrls();
        this.registerDeprecatedEditorUrlsForAndroid();
        void this.fetchNativeThemesOnMobile();
        this.eventDisposers.push(preferences.addEventObserver((event) => {
            if (event === PreferencesServiceEvent.PreferencesChanged) {
                this.postActiveThemesToAllViewers();
            }
        }));
        if (window.addEventListener) {
            window.addEventListener('focus', this.detectFocusChange, true);
        }
        else {
            window.attachEvent('onfocusout', this.detectFocusChange);
        }
        if (window.addEventListener) {
            window.addEventListener('blur', this.detectFocusChange, true);
        }
        else {
            window.attachEvent('onblur', this.detectFocusChange);
        }
        window.addEventListener('message', this.onWindowMessage, true);
    }
    deinit() {
        super.deinit();
        for (const viewer of this.viewers) {
            viewer.destroy();
        }
        this.viewers.length = 0;
        this.runWithPermissionsUseCase.deinit();
        this.desktopManager = undefined;
        this.items = undefined;
        this.features = undefined;
        this.sync = undefined;
        this.alerts = undefined;
        this.preferences = undefined;
        this.permissionDialogUIHandler = undefined;
        if (window) {
            window.removeEventListener('focus', this.detectFocusChange, true);
            window.removeEventListener('blur', this.detectFocusChange, true);
            window.removeEventListener('message', this.onWindowMessage, true);
        }
        ;
        this.detectFocusChange = undefined;
        this.onWindowMessage = undefined;
    }
    setPermissionDialogUIHandler(handler) {
        this.permissionDialogUIHandler = handler;
        this.runWithPermissionsUseCase.setPermissionDialogUIHandler(handler);
    }
    thirdPartyComponentsForArea(area) {
        return this.items.getDisplayableComponents().filter((component) => {
            return component.area === area;
        });
    }
    createComponentViewer(component, item, actionObserver) {
        var _a;
        const viewer = new ComponentViewer(component, {
            items: this.items,
            mutator: this.mutator,
            sync: this.sync,
            alerts: this.alerts,
            preferences: this.preferences,
            features: this.features,
            logger: this.logger,
        }, {
            url: (_a = this.urlForFeature(component)) !== null && _a !== void 0 ? _a : '',
            item,
            actionObserver,
        }, {
            environment: this.environment,
            platform: this.platform,
            componentManagerFunctions: {
                runWithPermissionsUseCase: this.runWithPermissionsUseCase,
                urlsForActiveThemes: this.urlsForActiveThemes.bind(this),
                setComponentPreferences: this.setComponentPreferences.bind(this),
                getComponentPreferences: this.getComponentPreferences.bind(this),
            },
        });
        this.viewers.push(viewer);
        return viewer;
    }
    destroyComponentViewer(viewer) {
        viewer.destroy();
        removeFromArray(this.viewers, viewer);
    }
    setDesktopManager(desktopManager) {
        this.desktopManager = desktopManager;
        this.configureForDesktop();
    }
    handleChangedComponents(components, source) {
        const acceptableSources = [
            PayloadEmitSource.LocalChanged,
            PayloadEmitSource.RemoteRetrieved,
            PayloadEmitSource.LocalDatabaseLoaded,
            PayloadEmitSource.LocalInserted,
        ];
        if (components.length === 0 || !acceptableSources.includes(source)) {
            return;
        }
        if (this.desktopManager) {
            const thirdPartyComponents = components.filter((component) => {
                const nativeFeature = FindNativeFeature(component.identifier);
                return nativeFeature ? false : true;
            });
            if (thirdPartyComponents.length > 0) {
                this.desktopManager.syncComponentsInstallation(thirdPartyComponents);
            }
        }
        const themes = components.filter((c) => c.isTheme());
        if (themes.length > 0) {
            this.postActiveThemesToAllViewers();
        }
    }
    addSyncedComponentItemObserver() {
        this.eventDisposers.push(this.items.addObserver([ContentType.TYPES.Component, ContentType.TYPES.Theme], ({ changed, inserted, removed, source }) => {
            const items = [...changed, ...inserted];
            this.handleChangedComponents(items, source);
            this.updateMobileRegisteredComponentUrls(inserted, removed);
        }));
    }
    updateMobileRegisteredComponentUrls(inserted, removed) {
        if (!isMobileDevice(this.device)) {
            return;
        }
        for (const component of inserted) {
            const feature = new UIFeature(component);
            const url = this.urlForFeature(feature);
            if (url) {
                this.device.registerComponentUrl(component.uuid, url);
            }
        }
        for (const component of removed) {
            this.device.deregisterComponentUrl(component.uuid);
        }
    }
    registerMobileNativeComponentUrls() {
        if (!isMobileDevice(this.device)) {
            return;
        }
        const nativeComponents = [...GetIframeEditors(), ...GetNativeThemes()];
        for (const component of nativeComponents) {
            const feature = new UIFeature(component);
            const url = this.urlForFeature(feature);
            if (url) {
                this.device.registerComponentUrl(feature.uniqueIdentifier.value, url);
            }
        }
    }
    /**
     * Gets all the native themes' CSS and stores them as `data:text/css;base64,...` URLs.
     */
    async fetchNativeThemesOnMobile() {
        if (!isMobileDevice(this.device)) {
            return;
        }
        try {
            for await (const theme of GetNativeThemes()) {
                const css = await this.device.getNativeThemeCSS(theme.identifier);
                if (css) {
                    const blob = new Blob([css], { type: 'text/css' });
                    const base64 = await blobToBase64(blob);
                    this.nativeThemesAsBase64[theme.identifier] = base64;
                }
            }
        }
        catch (error) {
            console.error(error);
        }
        finally {
            this.postActiveThemesToAllViewers();
        }
    }
    registerDeprecatedEditorUrlsForAndroid() {
        if (!isMobileDevice(this.device)) {
            return;
        }
        const deprecatedEditors = [...GetDeprecatedEditors()];
        for (const component of deprecatedEditors) {
            const feature = new UIFeature(component);
            const url = this.urlForFeature(feature);
            if (url) {
                this.device.registerComponentUrl(feature.uniqueIdentifier.value, url);
            }
        }
    }
    configureForDesktop() {
        if (!this.desktopManager) {
            throw new Error('Desktop manager is not defined');
        }
        this.desktopManager.registerUpdateObserver((component) => {
            const activeComponents = this.getActiveComponents();
            const isComponentActive = activeComponents.find((candidate) => candidate.uuid === component.uuid);
            if (isComponentActive && component.isTheme()) {
                this.postActiveThemesToAllViewers();
            }
        });
    }
    postActiveThemesToAllViewers() {
        for (const viewer of this.viewers) {
            viewer.postActiveThemes();
        }
    }
    urlForFeature(uiFeature) {
        const usecase = new GetFeatureUrl(this.desktopManager, this.environment, this.platform);
        return usecase.execute(uiFeature);
    }
    urlsForActiveThemes() {
        const themes = this.getActiveThemes();
        const urls = [];
        const isMobile = isMobileDevice(this.device);
        for (const theme of themes) {
            if (isMobile && theme.isNativeFeature) {
                /**
                 * Since native themes on mobile are stored in the app bundle and accessed as `file://` URLs,
                 * external editors cannot access them. To solve this, we store base64 encoded versions of the themes and send those to the editor instead of a file URL.
                 */
                const base64 = this.nativeThemesAsBase64[theme.featureIdentifier];
                if (base64) {
                    urls.push(base64);
                    continue;
                }
            }
            const url = this.urlForFeature(theme);
            if (url) {
                urls.push(url);
            }
        }
        return urls;
    }
    findComponentViewer(identifier) {
        return this.viewers.find((viewer) => viewer.identifier === identifier);
    }
    findComponentWithPackageIdentifier(identifier) {
        return this.items.getDisplayableComponents().find((component) => {
            return component.identifier === identifier;
        });
    }
    componentViewerForSessionKey(key) {
        return this.viewers.find((viewer) => viewer.sessionKey === key);
    }
    toggleOtherNonLayerableThemes(uiFeature) {
        const activeThemes = this.getActiveThemes();
        for (const candidate of activeThemes) {
            if (candidate.featureIdentifier === uiFeature.featureIdentifier) {
                continue;
            }
            if (!candidate.layerable) {
                this.removeActiveTheme(candidate);
            }
        }
    }
    async toggleTheme(uiFeature, skipEntitlementCheck = false) {
        this.logger.info('Toggling theme', uiFeature.uniqueIdentifier);
        if (this.isThemeActive(uiFeature)) {
            this.removeActiveTheme(uiFeature);
            return;
        }
        const featureStatus = this.features.getFeatureStatus(uiFeature.uniqueIdentifier);
        if (!skipEntitlementCheck && featureStatus !== FeatureStatus.Entitled) {
            return;
        }
        /* Activate current before deactivating others, so as not to flicker */
        this.addActiveTheme(uiFeature);
        /* Deactive currently active theme(s) if new theme is not layerable */
        if (!uiFeature.layerable) {
            await sleep(10);
            this.toggleOtherNonLayerableThemes(uiFeature);
        }
    }
    getActiveThemes() {
        const { features, uuids } = this.getActiveThemesIdentifiers();
        const thirdPartyThemes = uuids
            .map((uuid) => {
            const component = this.items.findItem(uuid.value);
            if (component) {
                return new UIFeature(component);
            }
            return undefined;
        })
            .filter(isNotUndefined);
        const nativeThemes = features
            .map((identifier) => {
            return FindNativeTheme(identifier.value);
        })
            .filter(isNotUndefined)
            .map((theme) => new UIFeature(theme));
        const entitledThemes = [...thirdPartyThemes, ...nativeThemes].filter((theme) => {
            return this.features.getFeatureStatus(theme.uniqueIdentifier) === FeatureStatus.Entitled;
        });
        return entitledThemes;
    }
    getActiveThemesIdentifiers() {
        const features = [];
        const uuids = [];
        const strings = new Set(this.preferences.getLocalValue(LocalPrefKey.ActiveThemes, []));
        for (const string of strings) {
            const nativeIdentifier = NativeFeatureIdentifier.create(string);
            if (!nativeIdentifier.isFailed()) {
                features.push(nativeIdentifier.getValue());
            }
            const uuid = Uuid.create(string);
            if (!uuid.isFailed()) {
                uuids.push(uuid.getValue());
            }
        }
        return { features, uuids };
    }
    async toggleComponent(component) {
        this.logger.info('Toggling component', component.uuid);
        if (this.isComponentActive(component)) {
            await this.removeActiveComponent(component);
        }
        else {
            await this.addActiveComponent(component);
        }
    }
    editorForNote(note) {
        const usecase = new EditorForNoteUseCase(this.items);
        return usecase.execute(note);
    }
    getDefaultEditorIdentifier(currentTag) {
        const usecase = new GetDefaultEditorIdentifier(this.preferences, this.items);
        return usecase.execute(currentTag).getValue();
    }
    doesEditorChangeRequireAlert(from, to) {
        const usecase = new DoesEditorChangeRequireAlertUseCase();
        return usecase.execute(from, to);
    }
    async showEditorChangeAlert() {
        const shouldChangeEditor = await this.alerts.confirm(c('B4.Notes.EditingUI.Confirmation').t `Doing so might result in minor formatting changes.`, c('B4.Notes.EditingUI.Confirmation').t `Are you sure you want to change this note's type?`, c('B4.Notes.EditingUI.Confirmation').t `Yes, change it`);
        return shouldChangeEditor;
    }
    async setComponentPreferences(uiFeature, preferences) {
        var _a;
        const mutablePreferencesValue = Copy((_a = this.preferences.getValue(PrefKey.ComponentPreferences, undefined)) !== null && _a !== void 0 ? _a : {});
        const preferencesLookupKey = uiFeature.uniqueIdentifier.value;
        mutablePreferencesValue[preferencesLookupKey] = preferences;
        await this.preferences.setValue(PrefKey.ComponentPreferences, mutablePreferencesValue);
    }
    getComponentPreferences(component) {
        const preferences = this.preferences.getValue(PrefKey.ComponentPreferences, undefined);
        if (!preferences) {
            return undefined;
        }
        const preferencesLookupKey = component.uniqueIdentifier.value;
        return preferences[preferencesLookupKey];
    }
    addActiveTheme(theme) {
        const activeThemes = this.preferences.getLocalValue(LocalPrefKey.ActiveThemes, []).slice();
        activeThemes.push(theme.uniqueIdentifier.value);
        this.preferences.setLocalValue(LocalPrefKey.ActiveThemes, activeThemes);
    }
    replaceActiveTheme(theme) {
        this.preferences.setLocalValue(LocalPrefKey.ActiveThemes, [theme.uniqueIdentifier.value]);
    }
    removeActiveTheme(theme) {
        const activeThemes = this.preferences.getLocalValue(LocalPrefKey.ActiveThemes, []);
        const filteredThemes = activeThemes.filter((activeTheme) => activeTheme !== theme.uniqueIdentifier.value);
        this.preferences.setLocalValue(LocalPrefKey.ActiveThemes, filteredThemes);
    }
    isThemeActive(theme) {
        if (this.features.getFeatureStatus(theme.uniqueIdentifier) !== FeatureStatus.Entitled) {
            return false;
        }
        const activeThemes = this.preferences.getLocalValue(LocalPrefKey.ActiveThemes, []);
        return activeThemes.includes(theme.uniqueIdentifier.value);
    }
    async addActiveComponent(component) {
        const activeComponents = this.preferences.getValue(PrefKey.ActiveComponents, []).slice();
        activeComponents.push(component.uuid);
        await this.preferences.setValue(PrefKey.ActiveComponents, activeComponents);
    }
    async removeActiveComponent(component) {
        var _a;
        const activeComponents = (_a = this.preferences.getValue(PrefKey.ActiveComponents, undefined)) !== null && _a !== void 0 ? _a : [];
        const filteredComponents = activeComponents.filter((activeComponent) => activeComponent !== component.uuid);
        await this.preferences.setValue(PrefKey.ActiveComponents, filteredComponents);
    }
    getActiveComponents() {
        var _a;
        const activeComponents = (_a = this.preferences.getValue(PrefKey.ActiveComponents, undefined)) !== null && _a !== void 0 ? _a : [];
        return this.items.findItems(activeComponents);
    }
    isComponentActive(component) {
        var _a;
        const activeComponents = (_a = this.preferences.getValue(PrefKey.ActiveComponents, undefined)) !== null && _a !== void 0 ? _a : [];
        return activeComponents.includes(component.uuid);
    }
}
